// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable/Clickable";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { clickable } from "@store/Themes/common/styleguide";
import { Step } from "@store/Library/Components/Bunzl/Steps/Steps";
import BankingDetailsForm from "@store/Library/Components/CreditForm/StepComponents/BankingDetailsForm";
import BasicDetailsForm, {
    CreditFormStepProps,
} from "@store/Library/Components/CreditForm/StepComponents/BasicDetailsForm";
import PrincipalAndTaxInformationForm from "@store/Library/Components/CreditForm/StepComponents/PrincipalAndTaxInformationForm";
import TradeReferencesForm from "@store/Library/Components/CreditForm/StepComponents/TradeReferencesForm";
import {
    CreditFormLabels,
    initialSteps,
    useCreditFormContext,
} from "@store/Library/Widgets/CreditForm/CreditFormContextContainer";
import { TermsSignatureData } from "@store/Library/Widgets/CreditForm/CreditFormForm";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { css } from "styled-components";
import CreditFormTermsAndConditions from "../CreditFormTermsAndConditions";

interface CreditFormConfirmFormProps {
    step: Step;
    hasSubmitted: boolean;
    setHasSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
    isFormValid: boolean;
    setSignatureData: React.Dispatch<React.SetStateAction<TermsSignatureData | undefined>>;
}

type CreditFormSteps = React.FC<CreditFormStepProps>;

export const componentToLabelDict: Record<typeof initialSteps[number]["label"], CreditFormSteps> = {
    "Basic Details": BasicDetailsForm,
    "Principal and Tax Information": PrincipalAndTaxInformationForm,
    "Trade References": TradeReferencesForm,
    "Banking Information": BankingDetailsForm,
    "Review and Submit": BankingDetailsForm,
};

interface CreditFormConfirmFormStyles {
    clickable?: InjectableCss;
    wrapper?: InjectableCss;
    tradeReferenceClickable?: InjectableCss;
}

const baseClickableStyles = {
    css: css`
        padding: 0.5rem;
        border-radius: 0.5rem;
        border: 1px solid transparent;
        flex-direction: column;
        :hover {
            box-shadow: 0px 0px 10px ${getColor("secondary")};
            border: 1px solid ${getColor("secondary")};
            transition: all 0.3s ease;
        }
        cursor: "pointer";
    `,
};

const creditFormConfirmFormStyles: CreditFormConfirmFormStyles = {
    clickable: baseClickableStyles,
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(20)};
        `,
    },
    tradeReferenceClickable: {
        css: css`
            ${baseClickableStyles.css}
            padding-inline: 0;
        `,
    },
};

const CreditFormConfirmForm: React.FC<CreditFormConfirmFormProps> = ({
    hasSubmitted,
    setHasSubmitted,
    isFormValid,
    setSignatureData,
}) => {
    const { setCurrentStep, steps } = useCreditFormContext();
    const stepsWithoutConfirm = steps.slice(0, steps.length - 1);
    const handleStepChange = (step: number) => {
        setCurrentStep(step);
    };
    if (!hasSubmitted) {
        setHasSubmitted(true);
    }

    return (
        <StyledWrapper {...creditFormConfirmFormStyles.wrapper}>
            {stepsWithoutConfirm.map((creditFormStep, index) => {
                const label = creditFormStep.label as CreditFormLabels;
                const Component = componentToLabelDict[label];
                const clickableStyles =
                    label === "Trade References"
                        ? creditFormConfirmFormStyles.tradeReferenceClickable
                        : creditFormConfirmFormStyles.clickable;
                return (
                    <Clickable key={label} onClick={() => handleStepChange(index)} {...clickableStyles}>
                        {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                        <Typography variant="h3">{translate(creditFormStep.label)}</Typography>
                        <Component hasSubmitted editable={false} stepIndex={index} />
                    </Clickable>
                );
            })}

            {isFormValid && <CreditFormTermsAndConditions setSignatureData={setSignatureData} />}
        </StyledWrapper>
    );
};

export default CreditFormConfirmForm;
